import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { User, AlertCircle, Search, Filter, RefreshCw, Users, CheckCircle, MessageSquare } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../components/config";
import Swal from "sweetalert2";
import AutoAssignmentService from "./AutoAssignmentService";

const AllocateCharts = () => {
  const [chats, setChats] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState({});
  const [chatCount, setChatCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAdmins();
    fetchChats();
    const interval = setInterval(fetchChats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchChats = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/clientservicechat/newChats`);
      const allChats = response.data;
      
      // Process chats for auto-assignment
      const processedChats = await Promise.all(
        allChats.map(async (chat) => {
          // Check if auto-assignment is enabled
          const settings = await AutoAssignmentService.getAssignmentSettings();
          
          if (settings.auto_assignment_enabled && settings.auto_assign_chats) {
            // Try auto-assign this chat
            const result = await AutoAssignmentService.autoAssignChat(
              chat.client_service_chat_id, 
              "general"
            );
            
            if (result.success) {
              // Chat was auto-assigned, don't show in unassigned list
              return null;
            } else {
              // Auto-assignment failed, show for manual assignment
              return { 
                ...chat, 
                assignmentNote: result.reason,
                autoAssignAttempted: true
              };
            }
          } else {
            // Auto-assignment disabled, show for manual assignment
            return { 
              ...chat, 
              assignmentNote: 'Auto-assignment disabled',
              autoAssignAttempted: false
            };
          }
        })
      );
      
      // Filter out auto-assigned chats
      const manualAssignmentChats = processedChats.filter(chat => chat !== null);
      const newChatCount = manualAssignmentChats.length;

      setChats(manualAssignmentChats);

      if (newChatCount > chatCount && newChatCount > 0) {
        showNewChatToast();
      }
      setChatCount(newChatCount);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/users`
      );
      const adminList = response.data.filter(
        (user) => user.role === "Executive"
      );
      setAdmins(adminList);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const showNewChatToast = () => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "info",
      title: "New Chats Available!",
      text: "You have new customer service chats to allocate.",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      background: "#002966",
      color: "#ffffff",
    });
  };

  const handleAdminChange = (client_service_chat_id, adminId) => {
    setSelectedAdmin({ client_service_chat_id, adminId });
    console.log("Selected Admin:", adminId);
  };

  const allocateAdmin = async () => {
    console.log("Selected Admin:", selectedAdmin.adminId);
    if (!selectedAdmin.adminId || !selectedAdmin.client_service_chat_id) return;
    try {
      setIsLoading(true);
      const response = await axios.put(
        `${API_URL}/clientservicechat/assign-admin/${selectedAdmin.client_service_chat_id}`,
        { admin_id: selectedAdmin.adminId }
      );
      if (response.status !== 200) {
        Swal.fire({
          icon: "success",
          title: "Admin Allocated Successfully!",
          showConfirmButton: false,
          timer: 1500,
          background: "#002966",
          color: "#ffffff",
        });
      }

      fetchChats();
      setSelectedAdmin({});
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Allocation Failed",
        text: "Please try again later",
        background: "#002966",
        color: "#ffffff",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-8 w-8 text-[#002966]" />
            <h1 className="text-3xl font-bold text-[#002966]">
              Chat Allocation Dashboard
            </h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchChats}
            className="flex items-center space-x-2 rounded-lg bg-[#002966] px-4 py-2 text-white transition-all hover:bg-[#001a4d]"
          >
            <RefreshCw className="h-5 w-5" />
            <span>Refresh</span>
          </motion.button>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-xl bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#002966]">
                Total UnAssigned Chats
              </h3>
              <MessageSquare className="h-6 w-6 text-[#ffc000]" />
            </div>
            <p className="mt-2 text-3xl font-bold text-[#002966]">
              {chatCount}
            </p>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-[#002966]">
                Available Admins
              </h3>
              <Users className="h-6 w-6 text-[#ffc000]" />
            </div>
            <p className="mt-2 text-3xl font-bold text-[#002966]">
              {admins.length}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {chats.map((chat) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={chat.client_service_chat_id}
              className="overflow-hidden rounded-xl bg-white p-6 shadow-lg transition-all hover:shadow-xl"
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-[#002966]">
                  Chat #{chat.client_service_chat_id}
                </h2>
                <span className="rounded-full bg-[#ffc000] px-3 py-1 text-sm text-[#002966]">
                  Active
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-600">
                    Customer ID:
                  </span>
                  <span className="text-[#002966]">{chat.customer_id}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-600">
                    Current Admin:
                  </span>
                  <span className="text-[#002966]">
                    {chat.admin_id || "Unassigned"}
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  <select
                    onChange={(e) =>
                      handleAdminChange(
                        chat.client_service_chat_id,
                        e.target.value
                      )
                    }
                    className="w-full rounded-lg border border-gray-300 p-2 focus:border-[#002966] focus:outline-none"
                  >
                    <option value="">Select Admin</option>
                    {admins.map((admin) => (
                      <option key={admin.userid} value={admin.userid}>
                        {admin.username}
                      </option>
                    ))}
                  </select>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={allocateAdmin}
                    // disabled={!selectedAdmin.client_service_chat_id || isLoading}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg bg-[#002966] py-2 text-white transition-all hover:bg-[#001a4d] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CheckCircle className="h-5 w-5" />
                    <span>{"Allocate Admin"}</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AllocateCharts;
